import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
// Through the index, so page tests can stub the layout.
import { DashboardLayout, Card, Button } from '.';
import { Breadcrumb } from './Breadcrumb';

/** One row in the overview: a staff member, or a group such as self-registered. */
export interface RegistrationSummaryEntry {
  key: string;
  /** The `registeredBy` value the row filters on. */
  filter: string;
  label: string;
  email?: string | null;
  badge?: { className: string; label: string };
  /** The three counts, in the order of `countLabels`. */
  counts: [number, number, number];
}

/** The registrar's staff role today; null when no longer staff. */
export const currentRoleBadge = (t: TFunction, role: 'admin' | 'support' | null) =>
  role === 'admin'
    ? { className: 'bg-soft-primary text-primary', label: t('adminCompanyRegistrations.currentRole.admin') }
    : role === 'support'
      ? { className: 'bg-soft-info text-info', label: t('adminCompanyRegistrations.currentRole.support') }
      : { className: 'bg-light text-dark border', label: t('adminCompanyRegistrations.currentRole.former') };

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

interface RegistrationReportProps<S, R> {
  /**
   * i18n prefix with title, subtitle, summary.{title,noRegistrars,total,columns.registrar},
   * filter.{label,all,unknown}, actions.{exportCsv,exporting}, errors.{summary,list,export}
   * and list.{heading,rowCount}.
   */
  i18nPrefix: string;
  breadcrumb: { label: string; path?: string }[];
  /** Id of the registrar select. */
  filterId: string;
  countLabels: [string, string, string];
  loadSummary: () => Promise<S>;
  /** The staff members first, then the groups; also the select's options. */
  summaryEntries: (summary: S) => RegistrationSummaryEntry[];
  /** Number of staff members, for the empty text. */
  registrarCount: (summary: S) => number;
  total: (summary: S) => number;
  loadRows: (registeredBy?: string) => Promise<R[]>;
  exportRows: (registeredBy?: string) => Promise<{ blob: Blob; filename: string }>;
  renderList: (rows: R[]) => React.ReactNode;
}

/**
 * Who on Etappio registered which contractor or client (S3, S8): an overview
 * per staff member that filters the list below, and a CSV export of the
 * current selection. Shared by the contractor and client pages.
 */
export function RegistrationReport<S, R>({
  i18nPrefix,
  breadcrumb,
  filterId,
  countLabels,
  loadSummary,
  summaryEntries,
  registrarCount,
  total,
  loadRows,
  exportRows,
  renderList
}: RegistrationReportProps<S, R>) {
  const { t } = useTranslation();
  const k = (key: string) => `${i18nPrefix}.${key}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const registeredBy = searchParams.get('registeredBy') ?? '';

  const [summary, setSummary] = useState<S | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [rows, setRows] = useState<R[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsFailed, setRowsFailed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummaryFailed(true);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The loader is fixed per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRowsLoading(true);
    setRowsFailed(false);
    loadRows(registeredBy || undefined)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch(() => {
        if (!cancelled) setRowsFailed(true);
      })
      .finally(() => {
        if (!cancelled) setRowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The loader is fixed per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredBy]);

  const selectFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('registeredBy', value);
    else next.delete('registeredBy');
    setSearchParams(next, { replace: true });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entries = useMemo(() => (summary ? summaryEntries(summary) : []), [summary, t]);

  const selectedLabel = registeredBy
    ? entries.find((row) => row.filter === registeredBy)?.label ?? t(k('filter.unknown'))
    : t(k('filter.all'));

  const handleExport = async () => {
    setIsExporting(true);
    setExportFailed(false);
    try {
      const { blob, filename } = await exportRows(registeredBy || undefined);
      downloadBlob(blob, filename);
    } catch {
      setExportFailed(true);
    } finally {
      setIsExporting(false);
    }
  };

  const summaryButton = (row: RegistrationSummaryEntry) => (
    <button
      type="button"
      className="btn btn-link company-registrations__select text-start text-break p-0"
      aria-pressed={registeredBy === row.filter}
      onClick={() => selectFilter(registeredBy === row.filter ? '' : row.filter)}
    >
      {row.label}
    </button>
  );

  const grandTotal = summary ? total(summary) : 0;

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <Breadcrumb items={breadcrumb} />
            <h4 className="fw-bold mb-1">{t(k('title'))}</h4>
            <p className="text-muted mb-0">{t(k('subtitle'))}</p>
          </div>
        </div>

        <Card className="mb-4" title={t(k('summary.title'))} icon="bar-chart-2">
          {summaryFailed ? (
            <div className="alert alert-danger mb-0" role="alert">{t(k('errors.summary'))}</div>
          ) : summaryLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
              {t('common.loading')}
            </div>
          ) : (
            <>
              {summary && registrarCount(summary) === 0 && (
                <p className="text-muted">{t(k('summary.noRegistrars'))}</p>
              )}
              <div className="list-group d-md-none" data-testid="registration-summary-cards">
                {entries.map((row) => (
                  <button
                    key={row.key}
                    type="button"
                    data-filter={row.filter}
                    className={`list-group-item list-group-item-action company-registrations__select-card${registeredBy === row.filter ? ' active' : ''}`}
                    aria-pressed={registeredBy === row.filter}
                    onClick={() => selectFilter(registeredBy === row.filter ? '' : row.filter)}
                  >
                    <span className="d-flex flex-wrap align-items-center gap-2">
                      <span className="fw-semibold text-break">{row.label}</span>
                      {row.badge && <span className={`badge ${row.badge.className}`}>{row.badge.label}</span>}
                    </span>
                    {row.email && <span className="d-block small text-break company-registrations__muted">{row.email}</span>}
                    <span className="row g-0 small mt-1">
                      {countLabels.map((label, index) => (
                        <span className="col-4" key={label}>
                          <span className="d-block company-registrations__muted">{label}</span>
                          <span className="fw-semibold">{row.counts[index]}</span>
                        </span>
                      ))}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  data-filter=""
                  className={`list-group-item list-group-item-action company-registrations__select-card d-flex justify-content-between fw-bold${registeredBy === '' ? ' active' : ''}`}
                  aria-pressed={registeredBy === ''}
                  onClick={() => selectFilter('')}
                >
                  <span>{t(k('summary.total'))}</span>
                  <span>{grandTotal}</span>
                </button>
              </div>
              <div className="table-responsive d-none d-md-block">
                <table className="table align-middle mb-0 company-registrations__summary" data-testid="registration-summary">
                  <thead>
                    <tr>
                      <th scope="col">{t(k('summary.columns.registrar'))}</th>
                      {countLabels.map((label) => (
                        <th scope="col" className="text-end" key={label}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((row) => (
                      <tr key={row.key} data-filter={row.filter} className={registeredBy === row.filter ? 'company-registrations__row--selected' : undefined}>
                        <td>
                          {summaryButton(row)}
                          {row.email && <div className="small text-muted text-break">{row.email}</div>}
                          {row.badge && <span className={`badge ${row.badge.className}`}>{row.badge.label}</span>}
                        </td>
                        {row.counts.map((count, index) => (
                          <td className="text-end" key={countLabels[index]}>{count}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={registeredBy === '' ? 'company-registrations__row--selected' : undefined}>
                      <th scope="row">
                        <button
                          type="button"
                          className="btn btn-link company-registrations__select text-start fw-bold p-0"
                          aria-pressed={registeredBy === ''}
                          onClick={() => selectFilter('')}
                        >
                          {t(k('summary.total'))}
                        </button>
                      </th>
                      <td className="text-end fw-bold" data-testid="registration-total">{grandTotal}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </Card>

        <Card>
          <div className="row g-3 align-items-end mb-3">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label" htmlFor={filterId}>
                {t(k('filter.label'))}
              </label>
              <select
                id={filterId}
                className="form-select company-registrations__control"
                value={registeredBy}
                onChange={(event) => selectFilter(event.target.value)}
              >
                <option value="">{t(k('filter.all'))}</option>
                {entries.map((row) => (
                  <option key={row.key} value={row.filter}>{row.label}</option>
                ))}
                {registeredBy && summary && !entries.some((row) => row.filter === registeredBy) && (
                  <option value={registeredBy}>{t(k('filter.unknown'))}</option>
                )}
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-8 d-flex justify-content-md-end">
              <Button
                variant="light-brand"
                className="admin-list-toolbar__btn"
                onClick={handleExport}
                disabled={isExporting}
                aria-label={isExporting ? t(k('actions.exporting')) : t(k('actions.exportCsv'))}
              >
                <i className="feather-download" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">
                  {isExporting ? t(k('actions.exporting')) : t(k('actions.exportCsv'))}
                </span>
              </Button>
            </div>
          </div>
          {exportFailed && (
            <div className="alert alert-danger" role="alert">{t(k('errors.export'))}</div>
          )}

          <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3">
            <h5 className="mb-0 text-break">{t(k('list.heading'), { selection: selectedLabel })}</h5>
            {!rowsLoading && !rowsFailed && (
              <span className="text-muted small">{t(k('list.rowCount'), { count: rows.length })}</span>
            )}
          </div>

          {rowsFailed ? (
            <div className="alert alert-danger mb-0" role="alert">{t(k('errors.list'))}</div>
          ) : rowsLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
              {t('common.loading')}
            </div>
          ) : (
            renderList(rows)
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
